import React, { useState } from 'react';
import { Clock, User, Edit3, Plus, Trash2, DollarSign, Calendar, Package, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react';

const RevisionTimelineCard = ({ revision, revisionNumber }) => {
  const [expanded, setExpanded] = useState(false);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'Not Set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return `${value.length} items`;
    if (typeof value === 'object') {
      // Special handling for payment objects
      if (value.mode && value.amount) {
        if (value.mode === 'Metal Credit') {
          return `Metal Credit: ${value.fine_wt || value.fine_grams}g fine`;
        }
        return `${value.mode}: ₹${value.amount}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatFieldName = (field) => {
    const fieldMap = {
      'item_type': 'Item Type',
      'weight_fixed': 'Weight',
      'weight_min': 'Min Weight',
      'weight_max': 'Max Weight',
      'making_value': 'Making Charges',
      'making_type': 'Making Type',
      'finish_color': 'Finish Color',
      'vendor_short_code': 'Vendor',
      'public_remark': 'Customer Note',
      'internal_remark': 'Internal Note',
      'single_delivery_date': 'Delivery Date',
      'order_date': 'Order Date',
      'customer_id': 'Customer',
      'advance_payments': 'Payments',
      'tag_number': 'Tag Number'
    };

    return fieldMap[field] || field
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'modified': return <Edit3 className="w-4 h-4" />;
      case 'payment_added': return <Plus className="w-4 h-4" />;
      case 'item_added': return <Plus className="w-4 h-4" />;
      case 'item_deleted': return <Trash2 className="w-4 h-4" />;
      case 'item_modified': return <Package className="w-4 h-4" />;
      case 'items_count_changed': return <Package className="w-4 h-4" />;
      default: return <Edit3 className="w-4 h-4" />;
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'modified': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'payment_added': return 'text-green-600 bg-green-50 border-green-200';
      case 'item_added': return 'text-green-600 bg-green-50 border-green-200';
      case 'item_deleted': return 'text-red-600 bg-red-50 border-red-200';
      case 'item_modified': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'items_count_changed': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const renderChangeDetail = (change, idx) => {
    if (change.type === 'modified') {
      // Check if this is a signature field change
      const isSignature = change.field === 'management_signature' || change.field === 'customer_signature';
      
      return (
        <div key={idx} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-1">
            <div className={`p-1.5 rounded-full ${getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 mb-1">{formatFieldName(change.field)}</p>
            {isSignature ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {change.old && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-red-700 font-semibold">Old:</span>
                    <img 
                      src={change.old} 
                      alt="Old signature" 
                      className="w-32 h-16 object-contain bg-red-50 border-2 border-red-200 rounded p-1"
                    />
                  </div>
                )}
                <span className="text-gray-400 text-xs sm:mx-2">→</span>
                {change.new && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-green-700 font-semibold">New:</span>
                    <img 
                      src={change.new} 
                      alt="New signature" 
                      className="w-32 h-16 object-contain bg-green-50 border-2 border-green-200 rounded p-1"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200 font-mono text-xs break-all">
                  {formatValue(change.old)}
                </span>
                <span className="text-gray-400 text-xs sm:mx-2">→</span>
                <span className="px-2 py-1 bg-green-50 text-green-700 rounded border border-green-200 font-mono text-xs break-all">
                  {formatValue(change.new)}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (change.type === 'item_modified') {
      // Build readable item display name
      const getItemDisplayName = () => {
        const itemType = change.item_type || 'Item';
        const purity = change.purity;
        const weight = change.weight;
        
        let displayName = itemType;
        if (purity) displayName += ` - ${purity}`;
        if (weight) displayName += ` (${weight}g)`;
        
        return displayName;
      };
      
      return (
        <div key={idx} className="py-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className={`p-1.5 rounded-full ${getChangeColor(change.type)}`}>
                {getChangeIcon(change.type)}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-purple-800 mb-2">
                📦 Item Modified: {getItemDisplayName()}
              </p>
              <div className="ml-4 space-y-2">
                {change.changes.map((itemChange, subIdx) => (
                  <div key={subIdx} className="pb-2 border-l-2 border-purple-200 pl-3">
                    <p className="text-xs font-medium text-gray-700 mb-1">{formatFieldName(itemChange.field)}</p>
                    <div className="flex flex-col gap-1 text-xs">
                      <span className="text-red-600">- {formatValue(itemChange.old)}</span>
                      <span className="text-green-600">+ {formatValue(itemChange.new)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'payment_added') {
      const isRefund = change.new?.transaction_type === 'REFUND';
      return (
        <div key={idx} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-1">
            <div className={`p-1.5 rounded-full ${isRefund ? 'text-red-600 bg-red-50 border-red-200' : getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold mb-1 ${isRefund ? 'text-red-800' : 'text-gray-800'}`}>
              {isRefund ? '↩️ Refund Added' : 'Payment Added'}
            </p>
            <div className={`px-3 py-2 border rounded text-xs ${isRefund ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <span className="font-semibold">{change.new?.mode}:</span>{' '}
              <span className={`font-bold ${isRefund ? 'text-red-700' : 'text-green-700'}`}>
                {isRefund ? '-' : ''}₹{change.new?.amount?.toLocaleString('en-IN')}
              </span>
              {change.new?.date && (
                <span className="text-gray-600 ml-2">on {new Date(change.new.date).toLocaleDateString('en-IN')}</span>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'payment_modified') {
      return (
        <div key={idx} className="py-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className={`p-1.5 rounded-full ${getChangeColor('modified')}`}>
                <Edit3 className="w-4 h-4" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-purple-800 mb-2">
                💳 Payment Modified {change.payment_id ? `(ID: ${change.payment_id})` : ''}
              </p>
              <div className="ml-4 space-y-2">
                {change.changes.map((paymentChange, subIdx) => {
                  const fieldName = formatFieldName(paymentChange.field);
                  const oldValue = paymentChange.old;
                  const newValue = paymentChange.new;
                  
                  // Special handling for amount changes
                  if (paymentChange.field === 'amount') {
                    const wasRefund = change.changes.find(c => c.field === 'transaction_type')?.old === 'REFUND';
                    const isRefund = change.changes.find(c => c.field === 'transaction_type')?.new === 'REFUND';
                    
                    return (
                      <div key={subIdx} className="pb-2 border-l-2 border-purple-200 pl-3">
                        <p className="text-xs font-medium text-gray-700 mb-1">{fieldName}</p>
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="text-red-600">
                            - {wasRefund ? '-' : ''}₹{oldValue?.toLocaleString('en-IN')}
                          </span>
                          <span className="text-green-600">
                            + {isRefund ? '-' : ''}₹{newValue?.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  
                  // Special handling for transaction_type changes
                  if (paymentChange.field === 'transaction_type') {
                    return (
                      <div key={subIdx} className="pb-2 border-l-2 border-purple-200 pl-3">
                        <p className="text-xs font-medium text-gray-700 mb-1">{fieldName}</p>
                        <div className="flex flex-col gap-1 text-xs">
                          <span className={`text-red-600 font-semibold ${oldValue === 'REFUND' ? 'bg-red-50 px-2 py-0.5 rounded' : ''}`}>
                            - {oldValue}
                          </span>
                          <span className={`text-green-600 font-semibold ${newValue === 'REFUND' ? 'bg-red-50 px-2 py-0.5 rounded' : ''}`}>
                            + {newValue}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  
                  // Default handling for other fields
                  return (
                    <div key={subIdx} className="pb-2 border-l-2 border-purple-200 pl-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">{fieldName}</p>
                      <div className="flex flex-col gap-1 text-xs">
                        <span className="text-red-600">- {formatValue(oldValue)}</span>
                        <span className="text-green-600">+ {formatValue(newValue)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'item_added') {
      const item = change.new_item;
      return (
        <div key={idx} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-1">
            <div className={`p-1.5 rounded-full ${getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800 mb-2">➕ New Item Added</p>
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 space-y-2 text-xs">
              {/* Item Photos */}
              {item.photos && item.photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.photos.map((photo, photoIdx) => (
                    <img 
                      key={photoIdx}
                      src={photo} 
                      alt={`Item photo ${photoIdx + 1}`}
                      className="w-16 h-16 object-cover rounded border-2 border-green-300"
                    />
                  ))}
                </div>
              )}
              <div><span className="font-semibold">Item:</span> {item.item_type}</div>
              {item.purity && <div><span className="font-semibold">Purity:</span> {item.purity}</div>}
              {item.weight_fixed && <div><span className="font-semibold">Weight:</span> {item.weight_fixed}g</div>}
              {item.making_value && <div><span className="font-semibold">Making:</span> {item.making_type === 'PERCENT' ? `${item.making_value}%` : item.making_type === 'PER_GRAM' ? `₹${item.making_value}/g` : item.making_type === 'WASTAGE' ? `${item.making_value} %Wst` : `₹${item.making_value}`}</div>}
              {item.quantity && <div><span className="font-semibold">Quantity:</span> {item.quantity}</div>}
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'item_deleted') {
      const item = change.old_item;
      return (
        <div key={idx} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-1">
            <div className={`p-1.5 rounded-full ${getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800 mb-2">❌ Item Removed</p>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 space-y-2 text-xs">
              {/* Item Photos */}
              {item.photos && item.photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {item.photos.map((photo, photoIdx) => (
                    <img 
                      key={photoIdx}
                      src={photo} 
                      alt={`Item photo ${photoIdx + 1}`}
                      className="w-16 h-16 object-cover rounded border-2 border-red-300"
                    />
                  ))}
                </div>
              )}
              <div><span className="font-semibold">Item:</span> {item.item_type}</div>
              {item.purity && <div><span className="font-semibold">Purity:</span> {item.purity}</div>}
              {item.weight_fixed && <div><span className="font-semibold">Weight:</span> {item.weight_fixed}g</div>}
              {item.making_value && <div><span className="font-semibold">Making:</span> {item.making_type === 'PERCENT' ? `${item.making_value}%` : item.making_type === 'PER_GRAM' ? `₹${item.making_value}/g` : item.making_type === 'WASTAGE' ? `${item.making_value} %Wst` : `₹${item.making_value}`}</div>}
              {item.quantity && <div><span className="font-semibold">Quantity:</span> {item.quantity}</div>}
            </div>
          </div>
        </div>
      );
    }

    if (change.type === 'items_count_changed') {
      return (
        <div key={idx} className="flex items-start gap-3 py-2">
          <div className="flex-shrink-0 mt-1">
            <div className={`p-1.5 rounded-full ${getChangeColor(change.type)}`}>
              {getChangeIcon(change.type)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 mb-1">Items Count Changed</p>
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200">
                {change.old_count} items
              </span>
              <span className="text-gray-400">→</span>
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded border border-green-200">
                {change.new_count} items
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const changes = revision.changes || [];
  const hasChanges = changes.length > 0;

  return (
    <div className="relative pl-8 pb-8 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-300 to-purple-100 last:bg-none"></div>
      
      {/* Timeline dot */}
      <div className="absolute left-0 top-2 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white text-xs font-bold">#{revisionNumber}</span>
      </div>

      {/* Card */}
      <div className="bg-white border-2 border-purple-200 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b-2 border-purple-200">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Clock className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-sm font-bold text-gray-800">
                  {new Date(revision.timestamp).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(revision.timestamp).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </span>
                {revision.provisional && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-300 rounded-full text-xs font-bold">
                    ⏳ Pending Review
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <span className="font-semibold">{revision.edited_by}</span>
                  {revision.approved_by && (
                    <span className="text-xs text-green-600 ml-2">
                      (Approved by {revision.approved_by})
                    </span>
                  )}
                </span>
              </div>
            </div>
            <span className="flex-shrink-0 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
              {changes.length} {changes.length === 1 ? 'change' : 'changes'}
            </span>
          </div>
          
          {hasChanges && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-white hover:bg-purple-50 rounded-lg border border-purple-200 transition-colors text-sm font-medium text-purple-700"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide Details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  View Details
                </>
              )}
            </button>
          )}
        </div>

        {/* Changes Details */}
        {expanded && hasChanges && (
          <div className="p-4 bg-gray-50 divide-y divide-gray-200">
            {changes.map((change, idx) => renderChangeDetail(change, idx))}
          </div>
        )}

        {!hasChanges && expanded && (
          <div className="p-4 bg-gray-50 text-center text-sm text-gray-500">
            No detailed changes recorded
          </div>
        )}
      </div>
    </div>
  );
};

export default RevisionTimelineCard;
